import { useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Bell, Heart, MessageCircle, UserPlus, Repeat, Coins, AtSign,
  AlertCircle, CheckCheck, ChevronRight, X,
} from 'lucide-react';

const TYPE_META = {
  follow:       { icon: UserPlus,       color: 'text-blue-400',   bg: 'bg-blue-500/10' },
  like:         { icon: Heart,          color: 'text-red-400',    bg: 'bg-red-500/10' },
  comment:      { icon: MessageCircle,  color: 'text-green-400',  bg: 'bg-green-500/10' },
  repost:       { icon: Repeat,         color: 'text-teal-400',   bg: 'bg-teal-500/10' },
  tip:          { icon: Coins,          color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  donation:     { icon: Coins,          color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  message:      { icon: MessageCircle,  color: 'text-primary',    bg: 'bg-primary/10' },
  mention:      { icon: AtSign,         color: 'text-purple-400', bg: 'bg-purple-500/10' },
  subscription: { icon: Coins,          color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  system:       { icon: AlertCircle,    color: 'text-orange-400', bg: 'bg-orange-500/10' },
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function notificationUrl(n) {
  const username = n.from_user?.username;
  switch (n.type) {
    case 'follow':  return username ? `/profile/${username}` : '/notifications';
    case 'like':
    case 'comment':
    case 'repost':
    case 'mention': return n.post_id ? `/post/${n.post_id}` : '/notifications';
    case 'tip':
    case 'donation': return '/wallet';
    case 'message': return username ? `/messages?user=${n.from_user_id}` : '/messages';
    default:        return '/notifications';
  }
}

export default function NotificationDropdown({ open, onClose, triggerRef }) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const panelRef = useRef(null);

  // Close on outside click (ignore trigger button)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (triggerRef?.current?.contains(e.target)) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, triggerRef]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const handleClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    onClose();
    navigate(notificationUrl(n));
  };

  const recent = notifications.slice(0, 8);

  return (
    <div
      ref={panelRef}
      className="fixed inset-x-2 top-[calc(var(--shell-top)+6px)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[380px] flex flex-col max-h-[min(480px,calc(var(--app-h)-var(--shell-top)-var(--shell-bottom)-16px))] overflow-hidden rounded-2xl border border-[hsl(var(--mg-rune)/0.3)] bg-card shadow-xl animate-in fade-in slide-in-from-top-2 duration-150 z-50"
      data-testid="notification-dropdown"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
        <h3 className="font-heading font-bold text-sm">Notifications</h3>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-primary hover:underline flex items-center gap-1"
              data-testid="mark-all-read-dropdown"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-notification-dropdown">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto overscroll-contain min-h-0 flex-1" data-testid="notification-dropdown-list">
        {recent.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No notifications yet
          </div>
        ) : (
          recent.map((n) => {
            const meta = TYPE_META[n.type] || TYPE_META.system;
            const Icon = meta.icon;
            const fromUser = n.from_user;
            return (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 ${
                  !n.is_read ? 'bg-primary/5' : ''
                }`}
                data-testid={`notification-item-${n.id}`}
              >
                {/* Type icon or user avatar */}
                <div className="relative shrink-0">
                  {fromUser?.avatar ? (
                    <Avatar className="w-9 h-9 border border-[hsl(var(--mg-rune)/0.2)]">
                      <AvatarImage src={fromUser.avatar} />
                      <AvatarFallback>{fromUser.display_name?.[0]}</AvatarFallback>
                    </Avatar>
                  ) : (
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${meta.bg}`}>
                      <Icon className={`w-4 h-4 ${meta.color}`} />
                    </div>
                  )}
                  {!n.is_read && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary border-2 border-card" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-snug">{n.content}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Footer */}
      <Link
        to="/notifications"
        onClick={onClose}
        className="flex items-center justify-center gap-1 px-4 py-2.5 text-xs font-medium text-primary border-t border-border/50 hover:bg-muted/30 transition-colors"
        data-testid="view-all-notifications"
      >
        View all notifications <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}
